import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { providersService } from '../../../shared/services/providers.service';
import type { DayOfWeek, WeeklyHoursDay } from '../../../shared/types';

/**
 * Public-facing opening-hours card on the provider profile.
 *
 * Source: GET /providers/{id}/hours. Backend stores days as 0=Sun … 6=Sat;
 * we render Monday-first here. Today's row is highlighted so the visitor
 * sees the current day's window at a glance.
 */
type Props = {
  providerId: string;
};

const DISPLAY_ORDER: { day: DayOfWeek; key: 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun' }[] = [
  { day: 1, key: 'mon' },
  { day: 2, key: 'tue' },
  { day: 3, key: 'wed' },
  { day: 4, key: 'thu' },
  { day: 5, key: 'fri' },
  { day: 6, key: 'sat' },
  { day: 0, key: 'sun' },
];

const TODAY: DayOfWeek = new Date().getDay() as DayOfWeek;

function findDay(days: WeeklyHoursDay[], target: DayOfWeek): WeeklyHoursDay | undefined {
  return days.find((d) => d.dayOfWeek === target);
}

export function OpeningHoursCard({ providerId }: Props) {
  const { t } = useTranslation();

  const query = useQuery({
    queryKey: ['provider', providerId, 'hours'],
    queryFn: () => providersService.getProviderHours(providerId),
    enabled: Boolean(providerId),
    staleTime: 60 * 1000,
  });

  const days = query.data?.days;
  // Render the 7-day grid as soon as we have ANY day data — even when all
  // days are marked closed. The previous logic dropped to the "no hours"
  // empty state in that case, which made it look like the provider had
  // never configured anything (misleading).
  const hasDays = (days?.length ?? 0) > 0;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t('hours.title')}
      </Typography>

      {query.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : query.isError ? (
        // Flaky mobile networks used to silently land on the "no hours"
        // empty state; surface the failure explicitly with a retry instead.
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<RefreshRoundedIcon />}
              onClick={() => query.refetch()}
            >
              {t('common.retry')}
            </Button>
          }
        >
          {t('hours.loadError')}
        </Alert>
      ) : hasDays ? (
        <Stack divider={<Box sx={{ height: '1px', bgcolor: 'divider' }} />}>
          {DISPLAY_ORDER.map(({ day, key }) => {
            const h = findDay(days!, day);
            const closed = !h || h.isClosed;
            const isToday = day === TODAY;
            return (
              <Stack
                key={day}
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ py: 1 }}
              >
                <Typography
                  sx={{
                    flex: 1,
                    fontWeight: isToday ? 700 : 400,
                    color: isToday ? 'text.primary' : 'text.secondary',
                  }}
                >
                  {t(`hours.days.${key}`)}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: isToday ? 700 : 500,
                    color: closed ? 'text.disabled' : 'text.primary',
                  }}
                >
                  {closed ? t('hours.closed') : `${h!.opensAt} – ${h!.closesAt}`}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      ) : (
        <Box sx={{ textAlign: 'center', py: 1 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              mx: 'auto',
              mb: 1.5,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.main}1F 0%, ${theme.palette.secondary.main}1F 100%)`,
              color: 'primary.main',
            }}
          >
            <AccessTimeRoundedIcon />
          </Box>
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
            {t('hours.emptyTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('hours.emptyHint')}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
